import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LanguageIcon from '@mui/icons-material/Language';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Box,
} from '@mui/material';
import { FC, MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { changeAppLocale } from '../../i18n';
import { AppLocale, isSupportedLocale, LOCALE_METADATA } from '../../i18n/config';
import { setStoredAnonymousLocale } from '../../i18n/localeResolver';
import { useAppLocale } from '../../i18n/useAppLocale';
import { useToast } from '../../context/Toast';
import { patchMe } from '../../services/user/user';
import LocaleFlag from '../localeFlag';

export const buildLocaleSearch = (search: string, locale: AppLocale): string => {
  const params = new URLSearchParams(search);
  params.set('locale', locale);
  const nextSearch = params.toString();
  return nextSearch ? `?${nextSearch}` : '';
};

export const persistAuthenticatedLocale = async (
  userId: string,
  locale: AppLocale,
  persist: typeof patchMe,
  updateCurrentUser: (partialUser: Record<string, unknown>) => void,
): Promise<void> => {
  await persist({ id: userId, locale });
  updateCurrentUser({ locale });
};

type LanguageSelectorDialogProps = {
  open: boolean;
  onClose: () => void;
};

export const LanguageSelectorDialog: FC<LanguageSelectorDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { user, updateCurrentUser } = useAuth();
  const { showToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const currentLocale = useAppLocale();
  const [savingLocale, setSavingLocale] = useState<AppLocale | null>(null);
  const isAuthenticated = Boolean(user?.user?.id);

  const updateAnonymousUrl = (locale: AppLocale) => {
    navigate({ search: buildLocaleSearch(location.search, locale) }, { replace: true });
  };

  const handleLocaleChange = async (value: unknown) => {
    if (!isSupportedLocale(value) || value === currentLocale || savingLocale) return;

    if (!isAuthenticated) {
      setStoredAnonymousLocale(value);
      await changeAppLocale(value);
      updateAnonymousUrl(value);
      onClose();
      return;
    }

    setSavingLocale(value);
    try {
      await persistAuthenticatedLocale(user.user.id, value, patchMe, updateCurrentUser);
      showToast(t('languageSelector.updateSuccess', { lng: value }), 'success');
      onClose();
    } catch {
      showToast(t('languageSelector.updateError'), 'error');
    } finally {
      setSavingLocale(null);
    }
  };

  return (
    <Dialog open={open} onClose={() => savingLocale === null && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>{t('languageSelector.title')}</DialogTitle>
      <List disablePadding>
        {LOCALE_METADATA.map(({ code, label, flag }) => (
          <ListItemButton
            key={code}
            selected={code === currentLocale}
            disabled={savingLocale !== null}
            onClick={() => void handleLocaleChange(code)}
          >
            <ListItemIcon sx={{ minWidth: 42 }}>
              <LocaleFlag src={flag} />
            </ListItemIcon>
            <ListItemText primary={label} />
            {savingLocale === code ? (
              <CircularProgress size={18} />
            ) : code === currentLocale ? (
              <CheckIcon fontSize="small" />
            ) : null}
          </ListItemButton>
        ))}
      </List>
    </Dialog>
  );
};

type LanguageSelectorMenuItemProps = {
  onOpen: () => void;
  disabled?: boolean;
};

export const LanguageSelectorMenuItem: FC<LanguageSelectorMenuItemProps> = ({ onOpen, disabled }) => {
  const { t } = useTranslation();

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onOpen();
  };

  return (
    <MenuItem onClick={handleOpen} disabled={disabled}>
      <ListItemIcon>
        <LanguageIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText primary={t('languageSelector.changeLanguage')} />
      <span aria-hidden="true">&gt;</span>
    </MenuItem>
  );
};

const LanguageSelector: FC = () => {
  const { t } = useTranslation();
  const currentLocale = useAppLocale();
  const [open, setOpen] = useState(false);
  const activeMetadata = LOCALE_METADATA.find(({ code }) => code === currentLocale);

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        aria-expanded={open}
        sx={{
          minWidth: '125px',
          padding: '0.62rem 0.9rem',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.75,
          border: '1px solid rgba(255, 255, 255, 0.18)',
          borderRadius: '999px',
          background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.10), rgba(255, 255, 255, 0.04))',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
          color: '#FFF',
          textTransform: 'none',
          whiteSpace: 'nowrap',
          flexShrink: 0,
          transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease, background 180ms ease',
          '&:hover, &:focus-visible': {
            background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.14), rgba(255, 255, 255, 0.06))',
            borderColor: 'rgba(255, 255, 255, 0.32)',
            boxShadow: '0 10px 28px rgba(0, 0, 0, 0.22)',
            transform: 'translateY(-1px)',
          },
          '& .language-selector-flag': {
            width: '1.25rem !important',
            height: '0.875rem !important',
            borderRadius: '0.2rem !important',
            boxShadow: '0 1px 4px rgba(0, 0, 0, 0.18)',
          },
          '& .language-selector-arrow': {
            fontSize: '1.1rem',
            transition: 'transform 180ms ease',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          },
        }}
        aria-label={String(t('languageSelector.changeLanguage'))}
      >
        <Box
          component="span"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, whiteSpace: 'nowrap' }}
        >
          {activeMetadata && <LocaleFlag src={activeMetadata.flag} className="language-selector-flag" />}
          <span className="language-selector-label">{activeMetadata?.label}</span>
        </Box>
        
      </Button>
      <LanguageSelectorDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
};

export default LanguageSelector;
